class PaginationLoadMore extends HTMLElement {
  constructor() {
    super();
    this.mode = this.dataset.mode;
    this.sectionId = this.dataset.sectionId;
    this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
    this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
    this.hybridAutoPages = parseInt(this.dataset.hybridAutoPages, 10) || 2;
    this.autoLoadedCount = 0;
    this.loading = false;
  }

  connectedCallback() {
    this.grid = document.getElementById('product-grid');
    this.button = this.querySelector('[data-load-more-button]');
    this.status = this.querySelector('[data-load-more-status]');
    if (!this.grid || !this.button) return;

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      this.loadNext();
    });

    if (this.mode === 'infinite' || this.mode === 'hybrid') {
      this.setupObserver();
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  setupObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio < 1 || this.loading) return;

        if (this.currentPage >= this.totalPages) {
          this.finish();
          return;
        }

        if (this.mode === 'hybrid' && this.autoLoadedCount >= this.hybridAutoPages) {
          this.stopAutoLoad();
          return;
        }

        this.loadNext({ auto: true });
      });
    }, { threshold: 1 });

    this.observer.observe(this.button);
  }

  stopAutoLoad() {
    if (this.observer) {
      this.observer.unobserve(this.button);
      this.observer.disconnect();
      this.observer = null;
    }
  }

  buildUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', this.sectionId);
    url.searchParams.set('page', page);
    return url.toString();
  }

  async loadNext({ auto = false } = {}) {
    if (this.loading) return;
    const nextPage = this.currentPage + 1;
    if (nextPage > this.totalPages) {
      this.finish();
      return;
    }

    this.loading = true;
    this.setLoadingState(true);

    try {
      const url = this.buildUrl(nextPage);
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status} ${url}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newGrid = doc.getElementById('product-grid');
      if (!newGrid) throw new Error('Response has no #product-grid');

      const newItems = Array.from(newGrid.querySelectorAll(':scope > .grid__item'));
      const startIndex = this.grid.children.length;
      const fragment = document.createDocumentFragment();
      newItems.forEach((item, i) => {
        item.style.setProperty('--index', startIndex + i);
        fragment.appendChild(item);
      });
      this.grid.appendChild(fragment);

      this.currentPage = nextPage;
      if (auto) this.autoLoadedCount += 1;
      this.announce(newItems.length);

      this.dispatchEvent(new CustomEvent('pagination-load-more:loaded', {
        bubbles: true,
        detail: { items: newItems, page: nextPage, totalPages: this.totalPages }
      }));

      if (this.currentPage >= this.totalPages) this.finish();
    } catch (err) {
      console.error('[pagination-load-more] load failed:', err);
      if (this.status) this.status.textContent = this.dataset.errorText || 'Unable to load more products.';
    } finally {
      this.loading = false;
      this.setLoadingState(false);
    }
  }

  setLoadingState(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    if (this.button) {
      this.button.classList.toggle('loading', isLoading);
      this.button.disabled = isLoading;
    }
  }

  announce(count) {
    if (!this.status) return;
    const template = this.dataset.loadedText || '%count% more products loaded';
    this.status.textContent = template.replace('%count%', count);
  }

  finish() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.classList.add('is-finished');
    this.remove();
  }
}

if (!customElements.get('pagination-load-more')) {
  customElements.define('pagination-load-more', PaginationLoadMore);
}
